import {
  useAssetDropdownInput,
  useGetAssetTypeOptions,
} from "#src/batteries-included-components/FilterAreas/assetFilters.helpers";
import {
  WorkflowTaskUserGroupDropdown,
  WorkflowTaskUserGroupDropdownGroupsType,
} from "#src/batteries-included-components/Filters/WorkflowTaskUserGroupDropdown/WorkflowTaskUserGroupDropdown";
import {
  FilterArea,
  useFilterAreaContentContext,
  useFilterAreaContext,
} from "#src/components/FilterArea";
import { FilterDrawer } from "#src/components/FilterDrawer";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  Accordion,
  DateSelectorInput,
  DropdownInput,
  PillToggleGroup,
  PillToggleVariants,
  StorageKeys,
  TextInput,
} from "@validereinc/common-components";
import {
  WorkflowCategoriesAdapter,
  WorkflowTaskBaseSchema,
  WorkflowTaskStatus,
  WorkflowTemplatesAdapter,
} from "@validereinc/domain";
import {
  Assets,
  Workflows,
  type AssetTypeSelectionType,
} from "@validereinc/domain-controllers";
import { asUTC, toTitleCaseString } from "@validereinc/utilities";
import { addDays, endOfDay, startOfDay, subDays } from "date-fns";
import React, { useEffect, useMemo } from "react";

const { WorkflowStepTypeOptions, WorkflowTaskTypeOptions } = Workflows.view;
const { AssetTypeSelection } = Assets.view;

export const FilterConfig = {
  search: {
    name: WorkflowTaskBaseSchema.keyof().Enum.name,
    placeholder: "Search Tasks...",
  },
  status: {
    name: WorkflowTaskBaseSchema.keyof().Enum.status,
  },
  assetName: {
    name: "assetName",
  },
  facility: {
    name: "facilityId",
  },
  equipment: {
    name: "equipmentId",
  },
  device: {
    name: "deviceId",
  },
  flow: {
    name: "flowId",
  },
  asset_group: {
    name: "assetGroupId",
  },
  assetType: {
    name: "assetType",
  },
  dueDate: {
    name: "workflow.due_date",
  },
  assignee: {
    name: "assignee",
  },
  workflowCategory: {
    name: "categoryId",
  },
  workflowTemplate: {
    name: "templateId",
  },
  stepType: {
    name: WorkflowTaskBaseSchema.keyof().Enum.step_type,
  },
  type: {
    name: WorkflowTaskBaseSchema.keyof().Enum.type,
  },
} as const;

export type WorkflowTasksTableFiltersType = {
  [FilterConfig.search.name]: string;
  [FilterConfig.status.name]: string[];
  [FilterConfig.dueDate.name]: { from: string; to: string };
  [FilterConfig.workflowCategory.name]?: string;
  [FilterConfig.stepType.name]?: string;
  [FilterConfig.type.name]?: string;
  workflow: Record<string, any>;
};

export type WorkflowTasksViewFiltersType = {
  [FilterConfig.assetType.name]: AssetTypeSelectionType;
  [FilterConfig.workflowTemplate.name]?: string;
  [FilterConfig.facility.name]: string[];
  [FilterConfig.equipment.name]: string[];
  [FilterConfig.device.name]: string[];
  [FilterConfig.flow.name]: string[];
  [FilterConfig.asset_group.name]: string[];
  [FilterConfig.assignee.name]?: [
    WorkflowTaskUserGroupDropdownGroupsType,
    string,
  ];
};

export const useRelativeDateRange = (dateRangeInDays = 30) =>
  useMemo(
    () => ({
      from: asUTC(startOfDay(subDays(new Date(), dateRangeInDays))),
      to: asUTC(endOfDay(addDays(new Date(), dateRangeInDays))),
    }),
    []
  );

export const WorkflowTasksViewFilterAreaContent = ({
  viewConfigStorageKey,
  defaultAssetType,
  showAssigneeFilter,
}: Pick<StorageKeys, "viewConfigStorageKey"> & {
  defaultAssetType?: AssetTypeSelectionType;
  showAssigneeFilter?: boolean;
}) => {
  const { handleOnChange } = useFilterAreaContentContext();
  const { localize } = useLocalization();
  const assetTypeOptions = useGetAssetTypeOptions();
  const [filters] = useStickyState<WorkflowTasksViewFiltersType>(
    {} as WorkflowTasksViewFiltersType,
    viewConfigStorageKey
  );
  const assetType = filters.assetType ?? defaultAssetType;

  const assetSelectionInput = useAssetDropdownInput(
    assetType,
    FilterConfig[assetType as keyof typeof FilterConfig]?.name,
    {
      isLabelShown: false,
      isFluid: false,
      isInline: true,
      description: `Filter by ${assetType === AssetTypeSelection.ALL_ASSETS ? "all assets" : `${localize(`${assetType}_plural`)}`}`,
      onChange: (val) =>
        handleOnChange(
          val,
          FilterConfig[assetType as keyof typeof FilterConfig]?.name
        ),
    }
  );

  return (
    <>
      <DropdownInput
        name={FilterConfig.assetType.name}
        isClearable={false}
        isInline
        labelKey="label"
        valueKey="value"
        placeholder={"Select Asset Type..."}
        options={assetTypeOptions}
        isSortedAlphabetically={false}
        description="Filter by Asset Type"
        onChange={(val) => handleOnChange(val, FilterConfig.assetType.name)}
      />
      {assetSelectionInput}
      <DropdownInput
        name={FilterConfig.workflowTemplate.name}
        onFetchData={async (payload) => {
          let { data } = await WorkflowTemplatesAdapter.getList({
            ...payload,
            filters: {
              ...(payload.searchTerm
                ? {
                    name: payload.searchTerm,
                  }
                : {}),
            },
          });

          // REVIEW: not a fan of the fact that we need to do this. A refactor of DropdownInput is needed.
          if (Array.isArray(payload.value)) {
            data = data.filter((d) => payload.value.includes(d.id));
          }

          return data;
        }}
        labelKey="name"
        valueKey="id"
        placeholder="Select Template..."
        isMulti
        label="Workflow Template"
        description="Filter by Workflow Template"
        isLabelShown={false}
        isOptionalTextShown={false}
        isInline
        onChange={(val) =>
          handleOnChange(val, FilterConfig.workflowTemplate.name)
        }
      />
      {showAssigneeFilter ? (
        <WorkflowTaskUserGroupDropdown
          name={FilterConfig.assignee.name}
          placeholder="Select Assignee..."
          label="Assignee"
          description="Filter by Assignee"
          isOptionalTextShown={false}
          viewConfigStorageKey={viewConfigStorageKey}
          isInline
          onChange={(val) => handleOnChange(val, FilterConfig.assignee.name)}
          isLabelShown={false}
        />
      ) : null}
    </>
  );
};

export const WorkflowTasksViewFilterArea = ({
  viewConfigStorageKey,
  defaultAssetType = AssetTypeSelection.FACILITY,
  ...rest
}: Pick<StorageKeys, "viewConfigStorageKey"> & {
  defaultAssetType?: AssetTypeSelectionType;
  showAssigneeFilter?: boolean;
}) => {
  return (
    <FilterArea.Root
      storageKey={viewConfigStorageKey}
      defaultValues={{
        [FilterConfig.assetType.name]: defaultAssetType,
      }}
      applyDefaultValues
    >
      <FilterArea.Container style={{ marginBottom: 16 }}>
        <FilterArea.Content>
          {() => (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
            >
              <WorkflowTasksViewFilterAreaContent
                viewConfigStorageKey={viewConfigStorageKey}
                defaultAssetType={defaultAssetType}
                {...rest}
              />
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};

const WorkflowTasksTableFilterAreaDrawerContent = ({
  viewConfigStorageKey,
  defaultAssetType,
}: Pick<StorageKeys, "viewConfigStorageKey"> & {
  defaultAssetType: AssetTypeSelectionType;
}) => {
  const { localize } = useLocalization();
  const [filters] = useStickyState<WorkflowTasksViewFiltersType>(
    {} as WorkflowTasksViewFiltersType,
    viewConfigStorageKey
  );
  const assetType = filters.assetType ?? defaultAssetType;

  const attributeFilters = useCustomAttributeFiltersV2({
    assetType:
      assetType !== AssetTypeSelection.ALL_ASSETS
        ? assetType
        : AssetTypeSelection.FACILITY,
    prefix: `workflow.${assetType}.custom_attributes`,
  });

  return (
    <>
      <DateSelectorInput
        {...FilterConfig.dueDate}
        variant="day"
        isRange
        isFluid
        label="Due Date"
        isOptionalTextShown={false}
        description="Search based on Workflow Due Date"
      />
      <DropdownInput
        name={FilterConfig.workflowCategory.name}
        onFetchData={async (payload) => {
          let { data } = await WorkflowCategoriesAdapter.getList({
            ...payload,
            filters: {
              ...(payload.searchTerm
                ? {
                    name: payload.searchTerm,
                  }
                : {}),
            },
          });

          // REVIEW: not a fan of the fact that we need to do this. A refactor of DropdownInput is needed.
          if (Array.isArray(payload.value)) {
            data = data.filter((d) => payload.value.includes(d.id));
          }
          return data;
        }}
        labelKey="name"
        valueKey="id"
        placeholder="Select Category..."
        isMulti
        isFluid
        label="Workflow Category"
        isOptionalTextShown={false}
      />
      {assetType !== "all_assets" ? (
        <Accordion defaultActiveKeys={[assetType]}>
          <Accordion.AccordionPanel
            dataKey={assetType}
            title={localize(`${assetType}_plural`)}
          >
            {...attributeFilters.customAttributeFilters}
          </Accordion.AccordionPanel>
        </Accordion>
      ) : null}
    </>
  );
};

const WorkflowTasksSingleWorkflowTableFilterAreaDrawerContent = () => {
  return (
    <>
      <DropdownInput
        name={FilterConfig.stepType.name}
        label="Action Step Type"
        placeholder="Select Type..."
        options={WorkflowStepTypeOptions}
        labelKey="label"
        valueKey="value"
      />
      <DropdownInput
        name={FilterConfig.type.name}
        label="Action Type"
        placeholder="Select Type..."
        options={WorkflowTaskTypeOptions}
        labelKey="label"
        valueKey="value"
      />
    </>
  );
};

const WorkflowTasksStatusPillToggles = ({
  onChange,
  isDisplayingMyTasks,
  isWithinAWorkflow,
}: {
  onChange: (val: string[]) => void;
  isDisplayingMyTasks: boolean;
  isWithinAWorkflow: boolean;
}) => {
  const { storedFilters, setStoredFilters } = useFilterAreaContext<{
    status: string[];
  }>();

  const pillToggles = [
    {
      name: "All",
      label: "All",
      value: "",
      isSelected:
        !storedFilters.status?.length || storedFilters.status?.includes(""),
      shouldSelectAll: true,
    },
    {
      name: WorkflowTaskStatus.OPEN,
      label: toTitleCaseString(WorkflowTaskStatus.OPEN, { intent: "ui" }),
      value: WorkflowTaskStatus.OPEN,
      variant: PillToggleVariants.PENDING,
      isSelected:
        storedFilters.status?.includes(WorkflowTaskStatus.OPEN) ?? false,
    },
    {
      name: WorkflowTaskStatus.COMPLETE,
      label: toTitleCaseString(WorkflowTaskStatus.COMPLETE, { intent: "ui" }),
      value: WorkflowTaskStatus.COMPLETE,
      variant: PillToggleVariants.GOOD,
      isSelected:
        storedFilters.status?.includes(WorkflowTaskStatus.COMPLETE) ?? false,
    },
    {
      name: WorkflowTaskStatus.DISMISSED,
      label: toTitleCaseString(WorkflowTaskStatus.DISMISSED, {
        intent: "ui",
      }),
      value: WorkflowTaskStatus.DISMISSED,
      variant: PillToggleVariants.NEUTRAL,
      isSelected:
        storedFilters.status?.includes(WorkflowTaskStatus.DISMISSED) ?? false,
    },
    {
      name: WorkflowTaskStatus.OVERDUE,
      label: toTitleCaseString(WorkflowTaskStatus.OVERDUE, { intent: "ui" }),
      value: WorkflowTaskStatus.OVERDUE,
      variant: PillToggleVariants.ATTENTION,
      isSelected:
        storedFilters.status?.includes(WorkflowTaskStatus.OVERDUE) ?? false,
    },
    {
      name: WorkflowTaskStatus.MISSED,
      label: toTitleCaseString(WorkflowTaskStatus.MISSED, { intent: "ui" }),
      value: WorkflowTaskStatus.MISSED,
      variant: PillToggleVariants.FAILURE,
      isSelected:
        storedFilters.status?.includes(WorkflowTaskStatus.MISSED) ?? false,
    },
  ];

  useEffect(() => {
    if (isDisplayingMyTasks && !isWithinAWorkflow) {
      const newStatus = [WorkflowTaskStatus.OPEN, WorkflowTaskStatus.OVERDUE];
      setStoredFilters({
        status: newStatus,
      });
      onChange(newStatus);
    } else {
      const newStatus = [""];
      setStoredFilters({
        status: newStatus,
      });
      onChange(newStatus);
    }
  }, [isDisplayingMyTasks, isWithinAWorkflow]);

  return (
    <PillToggleGroup
      name={FilterConfig.status.name}
      pills={pillToggles}
      onChange={onChange}
    />
  );
};

export const WorkflowTasksTableTitleDecorationFilterArea = ({
  filterConfigStorageKey,
  isDisplayingMyTasks,
  isWithinAWorkflow,
}: Pick<StorageKeys, "filterConfigStorageKey"> & {
  isDisplayingMyTasks: boolean;
  isWithinAWorkflow: boolean;
}) => {
  return (
    <FilterArea.Root storageKey={filterConfigStorageKey}>
      <FilterArea.Container aria-label="Filters for Workflow Tasks">
        <FilterArea.Content>
          {({ handleOnChange }) => (
            <div style={{ marginRight: 8, display: "flex", gap: 8 }}>
              <WorkflowTasksStatusPillToggles
                isDisplayingMyTasks={isDisplayingMyTasks}
                isWithinAWorkflow={isWithinAWorkflow}
                onChange={(val) =>
                  handleOnChange(val, FilterConfig.status.name)
                }
              />
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};

export const WorkflowTasksTableFilterArea = ({
  filterConfigStorageKey,
  viewConfigStorageKey,
  defaultAssetType = AssetTypeSelection.FACILITY,
  isWithinAWorkflow = false,
}: Pick<StorageKeys, "filterConfigStorageKey" | "viewConfigStorageKey"> & {
  defaultAssetType?: AssetTypeSelectionType;
  isWithinAWorkflow?: boolean;
}) => {
  return (
    <FilterArea.Root storageKey={filterConfigStorageKey}>
      <FilterArea.Container aria-label="Filters for Workflow Tasks">
        <FilterDrawer.Root>
          <FilterArea.Content>
            {({ handleOnChange }) => (
              <div style={{ marginRight: 8, display: "flex", gap: 8 }}>
                <TextInput
                  name={FilterConfig.search.name}
                  placeholder={FilterConfig.search.placeholder}
                  type="search"
                  isInline
                  onChange={(val) =>
                    handleOnChange(val, FilterConfig.search.name)
                  }
                />
              </div>
            )}
          </FilterArea.Content>
          <FilterDrawer.Trigger />
          <FilterDrawer.Content>
            {isWithinAWorkflow ? (
              <WorkflowTasksSingleWorkflowTableFilterAreaDrawerContent />
            ) : (
              <WorkflowTasksTableFilterAreaDrawerContent
                viewConfigStorageKey={viewConfigStorageKey}
                defaultAssetType={defaultAssetType}
              />
            )}
          </FilterDrawer.Content>
        </FilterDrawer.Root>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};
